<template>
   <v-app-bar color="white" elevation=0>
      <v-btn :href="frontendUrl">
         <v-app-bar-title>
            <img :src="inletsLogo" alt="inlets.ai" class="brand-logo-img" />
         </v-app-bar-title>
      </v-btn>
   </v-app-bar>
   <v-app>
      <v-main>
         <v-container class="fill-height">
            <v-row align="center" justify="center" class="fill-height">
               <v-col cols="12" sm="8" md="6">
                  <v-card class="pa-4">
                     <v-card-title class="text-left">
                     <h3>Create an Account</h3>
                     </v-card-title>
                     <v-card-text>
                        <v-alert
                           v-if="getSignupError"
                           type="error"
                           variant="tonal"
                           closable
                           class="mb-4"
                        >
                           {{ getSignupError }}
                        </v-alert>
                        <v-form v-model="isFormValid">
                           <v-text-field
                              v-model="first"
                              color="primary"
                              label="First name"
                              variant="outlined"
                              :rules="nameRules"
                           ></v-text-field>
                     
                           <v-text-field
                              v-model="last"
                              color="primary"
                              label="Last name"
                              variant="outlined"
                              :rules="nameRules"
                           ></v-text-field>
                     
                           <v-text-field
                              v-model="email"
                              color="primary"
                              label="Email"
                              variant="outlined"
                              :rules="emailRules"
                              prepend-inner-icon="mdi-email-outline"
                           ></v-text-field>
                     
                           <v-text-field
                              v-model="password"
                              color="primary"
                              label="Password"
                              :type="showPassword ? 'text' : 'password'"
                              placeholder="Enter your password"
                              variant="outlined"
                              :rules="passwordRules"
                              prepend-inner-icon="mdi-lock-outline"
                              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                              @click:append-inner="showPassword = !showPassword"
                           ></v-text-field>
                           <v-text-field
                              v-model="passwordConfirm"
                              color="primary"
                              label="Confirm password"
                              :type="showPasswordConfirm ? 'text' : 'password'"
                              placeholder="Confirm your password"
                              variant="outlined"
                              :rules="passwordConfirmRules"
                              prepend-inner-icon="mdi-lock-outline"
                              :append-inner-icon="showPasswordConfirm ? 'mdi-eye-off' : 'mdi-eye'"
                              @click:append-inner="showPasswordConfirm = !showPasswordConfirm"
                           ></v-text-field>
                           <v-select
                              v-model="country"
                              label="Country"
                              variant="outlined"
                              :rules="countryRules"
                              :items="countryItems"
                              item-title="name"
                              item-value="code"
                           ></v-select>
                        </v-form>
                     </v-card-text>
                     <v-card-actions class="px-6 pt-0 pb-4">
                        <v-row v-if="getSignupLoading" align="center" justify="center">
                           <v-progress-circular
                              :size="42"
                              :width="6"
                              color="primary"
                              indeterminate
                           ></v-progress-circular>
                        </v-row>
                        <v-row v-else>
                           <v-btn v-if="!isDemo" :disabled="!isFormValid" @click="onSignUp" color="orange">Create Account</v-btn>
                           <v-btn @click="toLogin" color="primary">{{ loginText }}</v-btn>
                        </v-row>
                     </v-card-actions>
                     <v-row v-if="!isDemo">
                        <v-divider class="mx-8 my-4">
                        </v-divider>
                     </v-row>
                     <v-row v-if="!isDemo" class="pb-6" justify="center">
                        <GoogleSignInButton
                           @success="handleGoogleLoginSuccess"
                           @error="handleGoogleLoginError"
                        ></GoogleSignInButton>
                     </v-row>
                  </v-card>
               </v-col>
            </v-row>
       </v-container>
     </v-main>
   </v-app>
 </template>
 
<script>
   import { mapActions, mapGetters } from "vuex";
   import router from "@/router";
   import { GoogleSignInButton } from "vue3-google-signin";
   import inletsLogo from "@/assets/images/inlets-logo-color.svg";

   export default {
      name: 'Signup',
      components: {
         GoogleSignInButton
      },
      computed: {
         ...mapGetters(["getAuthToken", "getUserEmail", "getUserID", "isLoggedIn", "getSignupError", "getSignupLoading"]),
         isDemo() {
            return process.env.NODE_ENV === 'demo'
         },
         loginText () {
            return this.isDemo ? 'Log into Demo Account' : 'Login Instead'
         },
         frontendUrl() {
            return process.env.VUE_APP_FRONTEND_URL
         }
      },
      data() {
         return {
            inletsLogo,
            isFormValid: false,
            email: "",
            password: "",
            passwordConfirm: "",
            showPassword: false,
            showPasswordConfirm: false,
            first: "",
            last: "",
            country: "US",
            nameRules: [
               v => !!v || "First name is required",
               v => (v && v.length <= 20) || "First name must be less than or equal to 20 characters",
            ],
            emailRules: [
               v => !!v || "Email is required",
               v => /.+@.+\..+/.test(v) || "Invalid email format",
            ],
            passwordRules: [
               v => !!v || "Password is required",
               v => (v && v.length >= 8) || "Password must be at least 8 characters",
            ],
            passwordConfirmRules: [
               v => !!v || "Please confirm your password",
               v => v === this.password || "Passwords do not match",
            ],
            countryItems: [
               { name: "United States", code: "US" },
               { name: "Canada", code: "CA" },
            ],
            countryRules: [
               v => !!v || "Country selection is required",
            ],
         };
      },
      methods: {
         ...mapActions(["registerUser", "googleSignUp"]),
         async onSignUp(event) {
            event.preventDefault();
            let data = {
                  user: {
                     email: this.email,
                     password: this.password,
                     first_name: this.first,
                     last_name: this.last,
                     country: this.country
                  },
            };
            await this.registerUser(data);
         },
         resetData() {
               this.email = "";
               this.password = "";
               this.passwordConfirm = "";
               this.showPassword = false;
               this.showPasswordConfirm = false;
               this.first = "";
               this.last = "";
         },
         toLogin() {
            router.push({ name: 'Login' });
         },
         async handleGoogleLoginSuccess(response) {
            this.$store.commit("setSignupError", null);
            try {
               await this.googleSignUp(response);
            } catch (error) {
               this.$store.commit(
                  "setSignupError",
                  error.response?.data?.message || "There was a problem signing up with Google."
               );
            }
         },
         handleGoogleLoginError() {
            console.error("Login failed");
         },
      }
   }
</script>
 